import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { ChatStatus } from "../game/chat-store";
import { isVoiceNoteSupported, type VoiceRef } from "../game/voice-note";
import { faTime } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { GameIcon } from "./GameIcon";
import { MicButton } from "./MicButton";
import { VoiceBubble } from "./VoiceBubble";

/** The minimal shape both a DM (`DmMessage`) and a table `ChatMessage` satisfy, so
 * the shared message list + composer render either without duplication. */
export interface ChatLineMessage {
  id: string;
  senderId: string;
  body: string;
  /** Epoch ms the message was sent (server-set on both DM and table messages) —
   * renders as the bubble's subtle `HH:MM` caption when present. */
  at?: number;
  kind?: "text" | "voice";
  voiceId?: string;
  voiceDurationMs?: number;
  /** Sender display name — set by the multi-party table chat so incoming bubbles
   * are attributable (rendered only under `showSenders`); DMs (two people, header
   * names the peer) never set it. */
  senderName?: string;
}

/**
 * The curated emoji set for the chat composer — themed groups, RTL-rendered as
 * a compact scrollable grid. Kept as plain string arrays (no ZWJ sequences that
 * older Android WebViews tofu on). Taps APPEND to the draft — never auto-send —
 * so emojis compose with text like any character.
 */
export const EMOJI_GROUPS: readonly { key: string; label: string; emojis: readonly string[] }[] = [
  {
    key: "faces",
    label: fa.chat.emojiGroups.faces,
    // prettier-ignore
    emojis: [
      "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "😉",
      "😍", "🥰", "😘", "😗", "😜", "🤪", "😝", "😎", "🤩", "🥳", "😏", "😌",
      "😢", "😭", "😤", "😠", "😡", "🤬", "😱", "😨", "🥺", "😳", "🙄", "😴",
      "😷", "🤒", "🥴", "🤯", "🤠", "🤗", "🤔", "🤫", "🤭", "😬", "😑", "😐",
    ],
  },
  {
    key: "hearts",
    label: fa.chat.emojiGroups.hearts,
    // prettier-ignore
    emojis: [
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕",
      "💞", "💓", "💗", "💖", "💘", "💝",
    ],
  },
  {
    key: "hands",
    label: fa.chat.emojiGroups.hands,
    // prettier-ignore
    emojis: [
      "👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "👇",
      "✋", "🤚", "🖖", "👋", "🤝", "👏", "🙌", "👐", "🙏", "💪", "✊", "👊",
    ],
  },
  {
    key: "games",
    label: fa.chat.emojiGroups.games,
    // prettier-ignore
    emojis: [
      "🃏", "🎴", "♠️", "♥️", "♦️", "♣️", "🎲", "🎯", "🎰", "🕹️", "🎮", "♟️",
      "🧩", "🀄", "🏆", "🥇", "🥈", "🥉", "🏅", "🎖️",
    ],
  },
  {
    key: "celebration",
    label: fa.chat.emojiGroups.celebration,
    // prettier-ignore
    emojis: [
      "🎉", "🎊", "🎈", "🎁", "🥂", "🍾", "✨", "🌟", "⭐", "🎆", "🎇", "🎂",
      "🍰", "💥", "🔥", "⚡", "💯", "🏁",
    ],
  },
  {
    key: "animals",
    label: fa.chat.emojiGroups.animals,
    // prettier-ignore
    emojis: [
      "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮",
      "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🦅", "🦉",
    ],
  },
  {
    key: "food",
    label: fa.chat.emojiGroups.food,
    // prettier-ignore
    emojis: [
      "☕", "🍵", "🧃", "🍩", "🍪", "🍫", "🍬", "🍭", "🍿", "🍕", "🍔", "🍟",
      "🌭", "🥪", "🍎", "🍉", "🍇", "🍓", "🍒", "🍑",
    ],
  },
];

/** The smiley toggle that opens/closes the emoji panel (inline `currentColor` SVG). */
function EmojiIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-7 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
    </svg>
  );
}

/**
 * The scrollable emoji grid panel (toggled by the composer's smiley button):
 * themed groups, 8-across RTL grid, app tokens. `onPick` appends the emoji to
 * the caller's draft.
 *
 * WHY IT FELT SLOW TO OPEN: it is ~170 buttons, and it was mounted and unmounted on
 * every toggle — so each tap paid the full DOM construction cost before anything could
 * appear, and it then simply POPPED into place (there was no entrance animation at all).
 * Now it (1) stays mounted once opened, so re-opens are instant, (2) rides a short
 * transform/opacity slide-up, and (3) marks each GROUP `content-visibility: auto`, so the
 * groups scrolled out of sight skip layout and paint entirely.
 */
function EmojiPicker({
  onPick,
  disabled,
  open,
}: {
  onPick: (emoji: string) => void;
  disabled?: boolean;
  /** Hidden (but still mounted) when false — see above. */
  open: boolean;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label={fa.chat.emoji}
      dir="rtl"
      hidden={!open}
      className={`max-h-52 overflow-y-auto border-t border-outline-variant/30 bg-surface-container-low px-3 py-2 ${
        open ? "emoji-panel-in" : ""
      }`}
      style={open ? undefined : { display: "none" }}
    >
      {EMOJI_GROUPS.map((group) => (
        <div
          key={group.key}
          className="mb-1"
          // Off-screen groups (the panel is a 208px scroller over ~170 buttons) cost
          // nothing to lay out or paint until they scroll in. Ignored by engines that
          // don't support it — a pure progressive enhancement.
          style={{ contentVisibility: "auto", containIntrinsicSize: "auto 120px" }}
        >
          <p className="m-0 px-1 py-1 font-caption text-caption text-on-surface-variant">
            {group.label}
          </p>
          <div className="grid grid-cols-8 gap-0.5">
            {group.emojis.map((e) => (
              <button
                key={e}
                type="button"
                aria-label={e}
                disabled={disabled}
                onClick={disabled ? undefined : () => onPick(e)}
                className="grid h-9 w-9 cursor-pointer appearance-none place-items-center rounded-lg border-0 bg-transparent text-xl leading-none active:scale-90 disabled:cursor-default disabled:opacity-40"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The scrollable message log — text bubbles and voice bubbles, own vs. peer by
 * `selfId`. Telegram-style RTL sides: bubbles the user SENT hug the RIGHT edge
 * (inline start → `self-start` under `dir="rtl"`), RECEIVED ones the LEFT
 * (`self-end`), each with a distinct tokened surface (primary-container for
 * own, surface-container-high for incoming — both themes), an asymmetric
 * bottom "tail" corner on its outer edge, and a subtle `HH:MM` time caption.
 * `data-mine` marks the side; shared by the DM thread and the table chat
 * panel. `showSenders` (the multi-party table chat) captions each INCOMING
 * bubble with its sender's name, so a 4-player thread stays attributable; DM
 * threads (two people, the header names the peer) leave it off.
 */
export function ChatMessageList({
  messages,
  selfId,
  connecting,
  emptyLabel,
  showSenders = false,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
}: {
  messages: readonly ChatLineMessage[];
  selfId: string;
  connecting: boolean;
  emptyLabel?: string;
  showSenders?: boolean;
  /** Older messages exist above — render the sentinel that fetches them. */
  hasMore?: boolean;
  loadingOlder?: boolean;
  /** Scrolled to the top with more to load. Omitted (table chat) → no paging at all. */
  onLoadOlder?: (() => void) | undefined;
}): ReactNode {
  // Keep the thread pinned to the newest message: scroll the log to the bottom
  // on open and whenever a message is sent OR received. Keyed on the count + the
  // last id, so a wholesale history replace (reconnect) re-pins too. Deliberately
  // ALWAYS pins the latest message into view (not "only when already near the
  // bottom" — the user wants the newest line shown). Shared by the DM thread and
  // the table-chat panel, so both surfaces get it from this one place.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const lastId = messages.length > 0 ? messages[messages.length - 1]?.id : undefined;
  // NO SOUND IS DECIDED HERE ANY MORE. The received-message chime used to be inferred
  // from renders — but a render cannot tell a HISTORY LOAD from a live line: the thread
  // mounts empty and the history arrives as a change, so simply OPENING a conversation
  // whose last line was the other person's played the chime at you (the reported beep).
  // The store fires it from `onMessage` — the one place that knows.
  const pinToBottom = (): void => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  /**
   * ⚠️ `useLayoutEffect`, NOT `useEffect` — THIS IS THE «loads from the beginning, then scrolls
   * to the end» THE USER SEES.
   *
   * The log renders top-down, so at scrollTop 0 the browser paints the OLDEST message of the
   * page. A `useEffect` runs after that paint, so the reader watches the thread appear at the
   * top and then jump to the bottom. A layout effect runs BEFORE the browser paints, so the
   * first frame is already at the newest message and there is nothing to see jumping.
   *
   * It also does the other half of paging: when an OLDER page is prepended, the content above
   * the reader grows and a naive pin would throw them to the bottom of a thread they were
   * scrolling up through. Growth above the viewport is exactly `scrollHeight - previous`, so
   * adding that to `scrollTop` leaves the message they were reading on the same pixel.
   */
  const firstId = messages.length > 0 ? messages[0]?.id : undefined;
  const prevFirstId = useRef<string | undefined>(undefined);
  const prevLastId = useRef<string | undefined>(undefined);
  const prevHeight = useRef(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prepended = prevFirstId.current !== undefined && firstId !== prevFirstId.current;
    const appended = lastId !== prevLastId.current;
    if (prepended && !appended) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else {
      pinToBottom();
    }
    prevFirstId.current = firstId;
    prevLastId.current = lastId;
    prevHeight.current = el.scrollHeight;
  }, [messages.length, firstId, lastId, connecting]);

  /**
   * Reaching the top asks for one page more.
   *
   * An IntersectionObserver rather than a scroll handler: it fires once when the sentinel
   * actually enters the viewport, costs nothing while the reader is anywhere else, and does not
   * read layout on every scroll event (this list already has three other things that must not
   * force synchronous layout — see the keyboard notes below).
   */
  const topRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = topRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !onLoadOlder || !hasMore) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadOlder();
      },
      { root, rootMargin: "120px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadOlder, hasMore, messages.length]);

  // Keyboard OPEN/close shrinks the log — re-pin to the newest line so the latest
  // messages stay visible above the keyboard.
  //
  // ONCE THE VIEWPORT STOPS MOVING, not on every frame of it: `visualViewport` reports
  // continuously across the keyboard's animation, and `pinToBottom` READS `scrollHeight`
  // (a forced synchronous layout) and WRITES `scrollTop` — doing that on every event, in
  // the middle of the shell being re-laid out, both cost a layout per frame and yanked
  // the list around while it moved. Debounced, the whole animation costs nothing and the
  // thread lands pinned to the newest line.
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    let settle = 0;
    const onResize = (): void => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => requestAnimationFrame(pinToBottom), 140);
    };
    vv.addEventListener("resize", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      window.clearTimeout(settle);
    };
  }, []);

  // …and re-pin whenever the LOG'S OWN BOX changes size. Watching the box itself is
  // platform-agnostic — iOS and Android report the keyboard completely differently, and
  // a ResizeObserver fires AFTER layout, so `scrollHeight` can never be stale. This is
  // what keeps the newest line from ending up half-hidden under the composer once the
  // keyboard settles (reported on iOS: "a little piece of the last message below").
  // It also covers the composer growing and the emoji panel opening.
  //
  // …AND whenever the CONTENT'S OWN HEIGHT changes. Watching only the box misses the
  // case that was reported on iOS — open a thread and the bottom sliver of the last
  // message sits below the fold. Nothing there resizes the BOX: the pin runs, and only
  // afterwards does the content settle to its final height (the web font swapping in,
  // a voice bubble laying out its waveform, a long line re-wrapping). The scroll offset
  // we set is then a few pixels short of the new bottom, and stays short — no box
  // change, no observer, no re-pin. Observing the content itself catches every one of
  // those, on every platform, because it is the thing that actually grew.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let settle = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => requestAnimationFrame(pinToBottom), 120);
    });
    observer.observe(el);
    if (content) observer.observe(content);
    return () => {
      observer.disconnect();
      window.clearTimeout(settle);
    };
  }, []);

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-4">
      <div ref={contentRef} className="flex flex-col gap-2.5">
        {/* Older-messages sentinel + its own quiet spinner. Rendered only when there IS more,
            so the reader never sees a loader for a conversation they have reached the start of. */}
        {hasMore ? (
          <div ref={topRef} className="flex justify-center py-1" aria-hidden={!loadingOlder}>
            {loadingOlder ? (
              <span
                role="status"
                aria-label={fa.chat.loadingOlder}
                className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-outline-variant border-t-secondary"
              />
            ) : null}
          </div>
        ) : null}
        {connecting ? (
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {fa.chat.connecting}
          </p>
        ) : messages.length === 0 ? (
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {emptyLabel ?? fa.chat.empty}
          </p>
        ) : (
          messages.map((m, i) => {
            const mine = m.senderId === selfId;
            // Caption an incoming bubble when the sender changes (not every line).
            const caption =
              showSenders && !mine && m.senderName && messages[i - 1]?.senderId !== m.senderId
                ? m.senderName
                : undefined;
            const bubble =
              m.kind === "voice" && m.voiceId ? (
                <VoiceBubble
                  key={m.id}
                  voiceId={m.voiceId}
                  durationMs={m.voiceDurationMs ?? 0}
                  mine={mine}
                  at={m.at}
                />
              ) : (
                <div
                  key={m.id}
                  data-mine={mine ? "true" : "false"}
                  className={[
                    "flex max-w-[78%] flex-col rounded-2xl px-3.5 py-2",
                    mine
                      ? "self-start bg-primary-container text-on-primary-container"
                      : "self-end bg-surface-container-high text-on-surface",
                  ].join(" ")}
                  // RTL "tail": tighten the bottom corner on the bubble's OUTER
                  // edge — sent sits right (inline start), received left.
                  style={
                    mine
                      ? { borderEndStartRadius: 4 }
                      : ({ borderEndEndRadius: 4 } as CSSProperties)
                  }
                >
                  <span className="whitespace-pre-wrap break-words font-body-md text-body-md">
                    {m.body}
                  </span>
                  {m.at ? (
                    <span
                      dir="ltr"
                      className="mt-0.5 self-end font-caption text-[10px] leading-none opacity-60"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {faTime(m.at)}
                    </span>
                  ) : null}
                </div>
              );
            if (!caption) return bubble;
            // The caption is its OWN list row (not a wrapper) so bubble sizing —
            // `max-w-[78%]` of the LIST — stays identical with or without it. It
            // aligns with the received side (left — `self-end` under RTL).
            return (
              <Fragment key={m.id}>
                <span className="-mb-1.5 self-end px-1 font-caption text-caption text-secondary">
                  {caption}
                </span>
                {bubble}
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Slim connection strip shared by the chat surfaces (the DM thread's header area
 * and the table-chat sheet). While the lifecycle re-establishes a dropped chat
 * socket it shows the transient «در حال اتصال دوباره…»; once the backoff attempts
 * are exhausted (`failed`) it becomes the compact error + «تلاش دوباره» (manual
 * cycle restart). Renders nothing while `connecting`/`open`/`error`.
 *
 * Deliberately NOT the full-screen game ConnectionOverlay: chat sockets register
 * with the lifecycle as `silent`, so the overlay stays reserved for game seats
 * and each chat surface shows its own inline state instead.
 */
export function ChatConnectionNotice({
  status,
  onRetry,
  canRetry = true,
}: {
  status: ChatStatus;
  onRetry?: () => void;
  /**
   * ⚠️ FALSE ONLY FOR A REFUSAL WE KNOW IS TERMINAL, and then the strip says WHAT happened
   * instead of «connection error» and offers no button. A table-chat join refused with
   * `TABLE_CHAT_NOT_A_MEMBER` means the table has disposed — «تلاش دوباره» could never succeed,
   * and it was offered anyway. Defaults true so every other chat surface is unchanged.
   */
  canRetry?: boolean;
}): ReactNode {
  if (status === "reconnecting") {
    return (
      <div
        role="status"
        className="flex flex-none items-center justify-center gap-2 bg-surface-container-low px-4 py-1.5"
      >
        <span
          aria-hidden="true"
          className="h-3 w-3 flex-none animate-spin rounded-full border-2 border-secondary border-t-transparent"
        />
        <span className="font-caption text-caption text-on-surface-variant">
          {fa.chat.reconnecting}
        </span>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div
        role="alert"
        className="flex flex-none items-center justify-center gap-3 bg-surface-container-low px-4 py-1.5"
      >
        <span className="font-caption text-caption text-error">
          {canRetry ? fa.chat.error : fa.chat.tableGone}
        </span>
        {onRetry && canRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="cursor-pointer appearance-none rounded-full border border-secondary/60 bg-secondary/15 px-3 py-1 font-caption text-caption text-secondary transition-transform active:scale-95"
          >
            {fa.chat.retry}
          </button>
        ) : null}
      </div>
    );
  }
  return null;
}

/**
 * The message composer. Visual order (RTL, right → left): the SEND button at
 * the inline-start edge with the mic beside it, then the text input, then the
 * emoji-picker toggle at the inline-end edge — send/voice and the emoji toggle
 * deliberately EXCHANGED sides (user decision) from the original layout. The
 * emoji toggle opens the themed grid panel (a tap APPENDS the emoji to the
 * draft); the mic (when the WebView can record) expands into the record →
 * send/cancel flow and calls `onSendVoice` with the uploaded note. Shared by
 * the DM thread and the table chat. `disabled` mutes the WHOLE surface (input,
 * send, emoji, mic) — e.g. a DM thread whose peer the user has blocked.
 */
export function ChatComposer({
  onSend,
  onSendVoice,
  emoji = true,
  disabled,
  bottomInset = "self",
}: {
  onSend: (body: string) => void;
  onSendVoice?: (ref: VoiceRef) => void;
  /** Render the emoji-picker toggle (default true). */
  emoji?: boolean;
  disabled?: boolean;
  /**
   * Who reserves the home-indicator inset below this composer — see the note on the row's
   * `paddingBottom`. `"self"` (the default, so a new render site is safe by construction) pads it
   * here; `"none"` is for a surface with something already padding it underneath, which today
   * means the DM thread's bottom nav.
   */
  bottomInset?: "self" | "none";
}): ReactNode {
  const [draft, setDraft] = useState("");
  const [recording, setRecording] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submit = (): void => {
    const body = draft.trim();
    if (!body || disabled) return;
    onSend(body);
    setDraft("");
    // Keep the keyboard OPEN so the user can fire off several messages in a row
    // without reopening it. Re-focusing runs inside the tap/keydown gesture, so iOS
    // keeps the keyboard up; the send button also preventDefaults its press below so
    // tapping it never steals focus from the input in the first place.
    inputRef.current?.focus();
  };

  // MOUNT ONCE, THEN JUST SHOW/HIDE. Building ~170 emoji buttons on every tap is what
  // made the panel feel sluggish to open; after the first open it is already in the DOM,
  // so the toggle is a pure style change.
  const emojiEverOpened = useRef(false);
  if (showEmoji) emojiEverOpened.current = true;
  const emojiOpen = Boolean(emoji) && showEmoji && !recording;

  return (
    <div className="flex-none">
      {emoji && emojiEverOpened.current && !recording ? (
        <EmojiPicker open={emojiOpen} onPick={(e) => setDraft((d) => d + e)} disabled={disabled} />
      ) : null}

      <div
        data-composer-row
        className="flex items-center gap-2 px-3 pt-2"
        style={{
          /**
           * WHO CLEARS THE HOME INDICATOR — reserved ONCE, by whatever is actually at the bottom.
           *
           * `"self"` (default): nothing is below us, so pad the safe area here. That is the
           * table-chat sheet, which is `Sheet flush` PRECISELY because this composer pads itself
           * ("padding it twice would stack two insets" — social-ui.tsx). Load-bearing: drop it
           * there and the composer sits on the home indicator.
           *
           * `"none"`: something below already reserves it — the DM thread, where the app's
           * in-flow `BottomNav` carries the identical `8px + max(env(...), --tg-safe-bottom)`.
           * Reserving it here as well was pure dead space between composer and nav, measured at
           * **17px + S** across seven viewports (17px at S=0, 51px at S=34, 65px at S=48) — the
           * reported «too much empty space», and worse on Android because S there is
           * `safeAreaInsets.bottom + contentSafeAreaInsets.bottom`: device inset PLUS Telegram's
           * own chrome.
           *
           * ⚠️ UNCONDITIONAL, and checked rather than assumed: the nav does NOT hide when the
           * keyboard opens. `theme.css` carries a `[data-keyboard="open"] [data-bottom-nav]
           * { display: none }` rule and two comments claiming it fires, but `data-keyboard` is
           * never written anywhere — verified by grep AND in a real browser under both simulated
           * platform keyboards (attribute null, nav `display: flex`, gap unchanged).
           */
          paddingBottom:
            bottomInset === "none"
              ? "8px"
              : "calc(8px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
        }}
      >
        {!recording ? (
          <button
            type="button"
            aria-label={fa.chat.send}
            disabled={disabled}
            // Don't let pressing the button blur the input (that dismisses the keyboard).
            onMouseDown={(e) => e.preventDefault()}
            onClick={submit}
            className="grid h-11 w-11 flex-none cursor-pointer place-items-center rounded-full border-0 bg-secondary text-ink-on-gold shadow-[0_4px_12px_rgba(232,194,104,0.3)] transition-transform active:scale-95 disabled:cursor-default disabled:opacity-40"
          >
            <GameIcon type="send" size={18} />
          </button>
        ) : null}

        {onSendVoice && isVoiceNoteSupported() ? (
          <MicButton onSendVoice={onSendVoice} onActiveChange={setRecording} disabled={disabled} />
        ) : null}

        {!recording ? (
          <input
            ref={inputRef}
            value={draft}
            disabled={disabled}
            placeholder={fa.chat.inputPlaceholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            className="min-w-0 flex-1 appearance-none rounded-full border border-surface-container-highest bg-surface-container-low px-4 py-2.5 font-body-md text-body-md text-on-surface outline-none transition-colors placeholder:text-outline-variant focus:border-secondary disabled:opacity-40"
          />
        ) : null}

        {emoji && !recording ? (
          <button
            type="button"
            aria-label={fa.chat.emoji}
            aria-expanded={showEmoji}
            disabled={disabled}
            onClick={() => setShowEmoji((v) => !v)}
            className={`grid h-11 w-11 flex-none cursor-pointer appearance-none place-items-center rounded-full border transition-colors active:scale-95 disabled:cursor-default disabled:opacity-40 ${
              showEmoji
                ? "border-secondary/60 bg-secondary/15 text-secondary"
                : "border-outline-variant/50 bg-surface-container-high text-on-surface-variant hover:text-on-surface"
            }`}
          >
            <EmojiIcon />
          </button>
        ) : null}
      </div>
    </div>
  );
}
